import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { subscriptionsAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { notify } from '../utils/notify';
import { HiCreditCard, HiCheck, HiSparkles, HiLightningBolt } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import { useCarga } from '../hooks/useCarga';
import ErrorAlCargar from '../components/ErrorAlCargar';
import ConfirmModal from '../components/ConfirmModal';
import { mensajeDeError } from '../utils/apiError';


/**
 * Como se llama cada consumo en pantalla.
 *
 * La pantalla construia la clave sobre la marcha —`subscription.action_` mas lo
 * que mandara el servidor— y el servidor manda `search`, `import` y
 * `ai_analysis`, mientras el archivo de idiomas tenia `action_product_search` y
 * `action_product_import`. Dos de las tres no existian, asi que se imprimia la
 * clave cruda: «Subscription.Action_search» en mitad de la pagina. Lo de las
 * mayusculas raras era la clase CSS `capitalize`, que ademas convertia
 * «Analisis de IA» en «Analisis De IA».
 *
 * Con el mapa explicito las claves vuelven a ser literales, que es lo unico que
 * el comprobador de traducciones sabe leer: si manana falta una, la caza antes
 * de llegar a produccion en vez de imprimirsela a alguien.
 *
 * Y si el servidor manda un consumo que no conocemos, se ensena su nombre tal
 * cual. Feo, pero honesto y visible — mejor que una clave de programador.
 */
const ETIQUETA_DE_ACCION = {
    search: 'subscription.action_product_search',
    import: 'subscription.action_product_import',
    ai_analysis: 'subscription.action_ai_analysis'
};

export default function Subscription() {
    const { user } = useAuthStore();
    const [searchParams] = useSearchParams();
    const [subscription, setSubscription] = useState(null);
    const [usage, setUsage] = useState(null);
    const { cargando: loading, error, ejecutar, reintentar } = useCarga();
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [reanudando, setReanudando] = useState(false);
    const { t } = useTranslation();

    const isPro = user?.subscription_tier === 'pro';
    const [precios, setPrecios] = useState({});

    useEffect(() => {
        fetchSubscription();

        // Handle success/cancel from Stripe
        if (searchParams.get('success')) {
            notify.success(t('subscription.activated_success'));
        }
        if (searchParams.get('cancelled')) {
            notify.info(t('subscription.checkout_cancelled'));
        }
    }, [searchParams]);

    // El precio sale del servidor, no de aquí.
    //
    // Estaba escrito a fuego —`price: 19`— mientras GET /subscriptions/plans
    // existía y no lo llamaba nadie. Eran dos definiciones de cuánto cuesta
    // Pro, y la que veía quien iba a pagar era justo la que NO está conectada
    // con Stripe: cambiar el precio allí dejaba esta pantalla mintiendo.
    //
    // Lo que sigue viniendo de i18n es el texto, que es lo suyo: el servidor
    // manda números y el idioma manda palabras.
    const fetchSubscription = () =>
        ejecutar(async () => {
            const [estado, planes] = await Promise.all([
                subscriptionsAPI.getStatus(),
                subscriptionsAPI.getPlans()
            ]);
            setSubscription(estado.data.data.subscription);
            setUsage(estado.data.data.usage);
            setPrecios(
                Object.fromEntries(
                    (planes.data.data.plans || []).map((p) => [p.id, p.price])
                )
            );
        });

    const handleUpgrade = async () => {
        setCheckoutLoading(true);
        try {
            const response = await subscriptionsAPI.checkout('pro');
            window.location.href = response.data.data.url;
        } catch (error) {
            notify.error(mensajeDeError(error, t('subscription.checkout_failed')));
        } finally {
            setCheckoutLoading(false);
        }
    };

    const handleManage = async () => {
        try {
            const response = await subscriptionsAPI.getPortal();
            window.location.href = response.data.data.url;
        } catch (error) {
            notify.error(t('subscription.portal_failed'));
        }
    };

    const handleCancelClick = () => {
        setShowCancelConfirm(true);
    };

    const confirmCancel = async () => {
        setShowCancelConfirm(false);
        try {
            await subscriptionsAPI.cancel();
            notify.success(t('subscription.cancel_success'));
            fetchSubscription();
        } catch (error) {
            // Con `mensajeDeError`: cancelar es dinero, y el motivo que da el
            // servidor --«no hay suscripción activa», por ejemplo-- se perdía
            // detrás de un genérico.
            notify.error(mensajeDeError(error, t('subscription.cancel_failed')));
        }
    };

    /**
     * Deshacer la cancelación.
     *
     * `POST /subscriptions/resume` existía en el servidor y `subscriptionsAPI.resume`
     * en el cliente, y **ninguna pantalla llamaba a ninguno de los dos**. Peor:
     * al cancelar, esta pantalla escondía el único botón que quedaba, así que
     * cancelar era irreversible desde la aplicación aunque el servidor supiera
     * deshacerlo. Quien se arrepentía tenía que salir a Stripe.
     */
    const reanudar = async () => {
        setReanudando(true);
        try {
            await subscriptionsAPI.resume();
            notify.success(t('subscription.resume_success'));
            fetchSubscription();
        } catch (error) {
            notify.error(mensajeDeError(error, t('subscription.resume_failed')));
        } finally {
            setReanudando(false);
        }
    };

    const plans = [
        {
            name: t('subscription.plan_free'),
            price: precios.free ?? 0,
            current: !isPro,
            features: [
                t('subscription.feature_free_1'),
                t('subscription.feature_free_2'),
                t('subscription.feature_free_3'),
                t('subscription.feature_free_4'),
                t('subscription.feature_free_5')
            ]
        },
        {
            name: t('subscription.plan_pro'),
            price: precios.pro,
            current: isPro,
            popular: true,
            features: [
                t('subscription.feature_pro_1'),
                t('subscription.feature_pro_2'),
                t('subscription.feature_pro_3'),
                t('subscription.feature_pro_4'),
                // `feature_pro_5` era «Conexiones de tiendas ilimitadas», y no
                // se puede hacer cierta mientras el único destino que publica
                // sea eBay: Shopify y WooCommerce están declarados sin una sola
                // operación y su botón está apagado. Vuelve cuando vuelvan.
                t('subscription.feature_pro_6'),
                t('subscription.feature_pro_7'),
                t('subscription.feature_pro_8')
            ]
        }
    ];

    if (loading) {
        return (
            <div className="max-w-4xl mx-auto">
                <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-1/4 animate-pulse mb-6"></div>
                <div className="grid md:grid-cols-2 gap-6">
                    {[1, 2].map((i) => (
                        <div key={i} className="h-96 bg-slate-200 dark:bg-slate-700 rounded-xl animate-pulse"></div>
                    ))}
                </div>
            </div>
        );
    }

    // Sin esto, un fallo de carga te enseñaba tu plan como si fuera gratuito.
    if (error) {
        return (
            <div className="max-w-4xl mx-auto space-y-6">
                <h1 className="page-title">{t('subscription.title')}</h1>
                <ErrorAlCargar error={error} onReintentar={reintentar} />
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-8">
            {/* Header */}
            <div>
                <h1 className="page-title flex items-center gap-2">
                    <HiCreditCard className="w-8 h-8 text-primary-500" />
                    {t('subscription.title')}
                </h1>
                <p className="text-slate-500 dark:text-slate-400 mt-1">
                    {t('subscription.subtitle')}
                </p>
            </div>

            {/* Current Plan Status */}
            {isPro && subscription && (
                <div className="card bg-gradient-to-r from-primary-500/10 to-accent-500/10 border-primary-500/20">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center">
                                <HiSparkles className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-slate-900 dark:text-white">{t('subscription.pro_active')}</h3>
                                {/* Sin fecha no se inventa una: `aFecha()` del
                                    servidor devuelve nulo cuando Stripe no la
                                    manda, y `new Date(null)` imprimia
                                    «Invalid Date» a quien paga. */}
                                <p className="text-sm text-slate-500">
                                    {!subscription.current_period_end
                                        ? t(subscription.cancel_at_period_end ? 'subscription.cancels_soon' : 'subscription.renews_unknown')
                                        : `${t(subscription.cancel_at_period_end ? 'subscription.cancels_on' : 'subscription.renews_on')} ${new Date(subscription.current_period_end).toLocaleDateString()}`}
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={handleManage} className="btn-secondary btn-sm">
                                {t('subscription.manage_billing')}
                            </button>
                            {/* Cancelada, se ofrece deshacerlo. Antes aquí no
                                quedaba nada: cancelar era irreversible desde la
                                aplicación aunque el servidor supiera reanudar. */}
                            {subscription.cancel_at_period_end ? (
                                <button
                                    type="button"
                                    onClick={reanudar}
                                    disabled={reanudando}
                                    className="btn-primary btn-sm disabled:opacity-50"
                                >
                                    {reanudando ? t('settings.saving') : t('subscription.resume_button')}
                                </button>
                            ) : (
                                <button onClick={handleCancelClick} className="btn-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg px-4">
                                    {t('subscription.cancel_button')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Usage Stats */}
            {usage && !isPro && (
                <div className="card">
                    <h3 className="section-title mb-4">{t('subscription.todays_usage')}</h3>
                    <div className="grid grid-cols-3 gap-4">
                        {Object.entries(usage).map(([action, data]) => (
                            <div key={action} className="text-center p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                                <p className="text-2xl font-bold text-slate-900 dark:text-white">
                                    {data.used}/{data.limit === 'unlimited' ? '∞' : data.limit}
                                </p>
                                <p className="text-sm text-slate-500 mt-1">
                                    {ETIQUETA_DE_ACCION[action] ? t(ETIQUETA_DE_ACCION[action]) : action}
                                </p>
                                {data.limit !== 'unlimited' && (
                                    <div className="mt-2 h-1.5 bg-slate-200 dark:bg-slate-600 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full rounded-full ${data.used >= data.limit ? 'bg-red-500' : 'bg-primary-500'
                                                }`}
                                            style={{ width: `${Math.min((data.used / data.limit) * 100, 100)}%` }}
                                        ></div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Plans */}
            <div className="grid md:grid-cols-2 gap-6">
                {plans.map((plan) => (
                    <div
                        key={plan.name}
                        className={`card relative ${plan.popular
                                ? 'border-primary-500 dark:border-primary-400 bg-gradient-to-b from-primary-50 to-white dark:from-primary-900/20 dark:to-slate-800'
                                : ''
                            }`}
                    >
                        {plan.popular && (
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full text-sm text-white font-medium">
                                {t('subscription.most_popular')}
                            </div>
                        )}

                        <div className="mb-6">
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white">{plan.name}</h3>
                            <div className="mt-2">
                                <span className="text-4xl font-bold text-slate-900 dark:text-white">
                                    {plan.price === undefined ? '—' : `$${plan.price}`}
                                </span>
                                <span className="text-slate-500">/{t('subscription.month')}</span>
                            </div>
                        </div>

                        <ul className="space-y-3 mb-8">
                            {plan.features.map((feature, i) => (
                                <li key={i} className="flex items-start gap-2">
                                    <HiCheck className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                                    <span className="text-slate-600 dark:text-slate-400">{feature}</span>
                                </li>
                            ))}
                        </ul>

                        {plan.current ? (
                            <button disabled className="btn-secondary w-full opacity-50 cursor-not-allowed">
                                {t('subscription.current_plan')}
                            </button>
                        ) : plan.name === 'Pro' ? (
                            <button
                                onClick={handleUpgrade}
                                disabled={checkoutLoading}
                                className="btn-primary w-full"
                            >
                                {checkoutLoading ? (
                                    <svg className="animate-spin w-5 h-5 mx-auto" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                    </svg>
                                ) : (
                                    <>
                                        <HiLightningBolt className="w-5 h-5 mr-2" />
                                        {t('subscription.upgrade_to_pro')}
                                    </>
                                )}
                            </button>
                        ) : (
                            <button disabled className="btn-secondary w-full opacity-50 cursor-not-allowed">
                                {t('subscription.free_forever')}
                            </button>
                        )}
                    </div>
                ))}
            </div>

            {/* FAQ */}
            <div className="card">
                <h3 className="section-title mb-4">{t('subscription.faq_title')}</h3>
                <div className="space-y-4">
                    <div>
                        <h4 className="font-medium text-slate-900 dark:text-white">{t('subscription.faq_1_q')}</h4>
                        <p className="text-sm text-slate-500 mt-1">
                            {t('subscription.faq_1_a')}
                        </p>
                    </div>
                    <div>
                        <h4 className="font-medium text-slate-900 dark:text-white">{t('subscription.faq_2_q')}</h4>
                        <p className="text-sm text-slate-500 mt-1">
                            {t('subscription.faq_2_a')}
                        </p>
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={showCancelConfirm}
                onClose={() => setShowCancelConfirm(false)}
                onConfirm={confirmCancel}
                title={t('common.confirm_title', 'Confirmar acción')}
                message={t('subscription.cancel_confirm', '¿Estás seguro de que quieres cancelar tu suscripción?')}
                confirmText={t('common.confirm', 'Confirmar')}
            />
        </div>
    );
}
